import { useMemo, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Text, TouchableRipple } from 'react-native-paper';

import { MyLocationIcon } from '@/assets';
import {
  MeineRegionRadiusModal,
  type MeineRegionRadiusModalHandle
} from '@/features/geo-area/components/MeineRegionRadiusModal';
import { useRegionalfunkRadiusStore } from '@/features/geo-area/store/useRegionalfunkRadiusStore';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const ROW_HEIGHT = 48;
const ROW_MARGIN_TOP = 4;

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    row: {
      height: ROW_HEIGHT,
      marginTop: ROW_MARGIN_TOP,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingHorizontal: 12
    },
    button: { borderRadius: 32 },
    buttonHover: { backgroundColor: theme.stateLayers.hover },
    buttonInner: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingLeft: 12
    },
    buttonLabel: {
      fontSize: 16,
      lineHeight: 24,
      fontWeight: '500',
      letterSpacing: 0.15,
      color: theme.colors.primary
    },
    icon: { padding: 8 }
  });

export const RadiusFilter: FC = () => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.GEO_AREA, {
    keyPrefix: 'screens.myRegion'
  });

  const radiusKm = useRegionalfunkRadiusStore((s) => s.meineRegionRadiusKm);
  const modalRef = useRef<MeineRegionRadiusModalHandle>(null);
  const [hover, setHover] = useState(false);

  return (
    <View style={styles.row}>
      <TouchableRipple
        onPress={() => modalRef.current?.open()}
        onHoverIn={() => setHover(true)}
        onHoverOut={() => setHover(false)}
        rippleColor={theme.rippleColors.onSurface}
        borderless
        style={[styles.button, hover && styles.buttonHover]}
      >
        <View style={styles.buttonInner}>
          <Text style={styles.buttonLabel}>
            {t('filters.radiusKm', { radiusKm })}
          </Text>
          <View style={styles.icon}>
            <MyLocationIcon
              fill={theme.colors.primary}
              width={20}
              height={20}
            />
          </View>
        </View>
      </TouchableRipple>

      <MeineRegionRadiusModal ref={modalRef} />
    </View>
  );
};
